// Tutorial interactivo (< 3 min). Enseña con acciones, no con bloques de texto.
// Prepara una mano fija y guía paso a paso detectando el progreso en el tablero.
import { EVENTS } from '../utils/constants.js';
import { normalizeForLookup } from '../utils/unicode.js';

// `place` (solo afecta en VERTICAL): dónde se coloca la tarjeta para NO tapar los objetos que
// el paso explica. 'center' en la intro/resumen; 'top' en los pasos de acción (tablero/mano/
// botones están en el centro y abajo, así que la tarjeta sube).
const STEPS = [
  { text: '🎯 <b>Objetivo:</b> construye palabras con tus fichas. <b>Gana quien se quede antes SIN fichas</b> en la mano.', place: 'center' },
  { text: 'Arrastra <b>CA</b> y <b>SA</b> a la mesa para construir <b>CASA</b>.', goalWord: 'casa', place: 'top' },
  { text: '¡Bien! Ahora arrastra <b>S</b> al final para evolucionar <b>CASA → CASAS</b>.', goalWord: 'casas', place: 'top' },
  { text: '🎴 ¿No puedes jugar? Pulsa <b>Pasar</b>: robas 1 ficha y cedes el turno.<br>Al <b>colocar</b> fichas <b>no</b> robas, así vas vaciando tu mano.', place: 'top' },
  { text: '🧩 <b>Mover y separar en la mesa:</b><br>• <b>Mover la palabra entera:</b> arrastra su <b>primera</b> sílaba de <b>lado a lado</b>.<br>• <b>Separar / desbloquear una sílaba</b> (incluida la <b>primera</b>): <b>tira de ella hacia abajo</b> y quedará suelta.<br>• Suéltala <b>junto a otra palabra</b> para unirla, o en un <b>hueco libre</b> para dejarla aparte.', place: 'top' },
  { text: 'Cuando tus palabras sean válidas (✓), pulsa <b>Confirmar</b> para puntuar y ceder el turno.', goalConfirm: true, place: 'top' },
  { text: '✅ <b>¡Ya sabes jugar!</b><br>• <b>Confirmar</b>: puntúa tu jugada.<br>• <b>Pasar</b>: roba 1 ficha y cede turno.<br>• <b>Reiniciar / Deshacer</b>: corrigen la jugada actual.<br>• <b>Mover palabra:</b> arrastra su 1ª sílaba de lado. <b>Separar una sílaba:</b> tira de ella hacia abajo.<br>El primero que se queda sin fichas gana. 🏆', recap: true, place: 'center' },
];

export class TutorialManager {
  constructor(root, { bus, game, onFinish }) {
    this.root = root;
    this.bus = bus;
    this.game = game;
    this.onFinish = onFinish;
    this.stepIndex = 0;
    this.el = document.createElement('div');
    this.el.className = 'tutorial-coach hidden';
    this.root.appendChild(this.el);
  }

  start() {
    this.stepIndex = 0;
    this._off = this.bus.on(EVENTS.BOARD_CHANGED, () => this._check());
    this._offConfirm = this.bus.on(EVENTS.TURN_CONFIRMED, () => this._advanceIfConfirm());
    this.el.classList.remove('hidden');
    this._show();
  }

  _show() {
    const step = STEPS[this.stepIndex];
    if (!step) return this.finish();
    // Coloca la tarjeta según el paso (en vertical) para no tapar los objetos que explica.
    this.el.classList.remove('coach-center', 'coach-top', 'coach-bottom');
    this.el.classList.add(`coach-${step.place || 'center'}`);
    this.el.innerHTML = `
      <div class="coach-card">
        <div class="coach-step">Paso ${this.stepIndex + 1} de ${STEPS.length}</div>
        <p>${step.text}</p>
        <div class="coach-actions">
          ${step.recap ? '' : '<button class="btn btn-sm" data-skip>Saltar tutorial</button>'}
          ${step.goalConfirm ? '' : `<button class="btn btn-sm btn-primary" data-next>${step.recap ? '¡A jugar! 🎉' : 'Siguiente ▶'}</button>`}
        </div>
      </div>`;
    this.el.querySelector('[data-skip]')?.addEventListener('click', () => this.finish());
    this.el.querySelector('[data-next]')?.addEventListener('click', () => this._next());
  }

  _check() {
    const step = STEPS[this.stepIndex];
    if (!step) return;
    const words = this.game.board.words.map((w) => normalizeForLookup(this.game.board.wordText(w)));
    if (step.goalWord && words.includes(step.goalWord)) this._next();
    else if (step.goalAny && this.game.board.words.length >= 2) this._next();
  }

  _advanceIfConfirm() {
    const step = STEPS[this.stepIndex];
    if (step?.goalConfirm) this._next(); // avanza al resumen de reglas
  }

  _next() {
    this.stepIndex += 1;
    if (this.stepIndex >= STEPS.length) return this.finish();
    this._show();
  }

  finish() {
    this._off?.();
    this._offConfirm?.();
    this.el.classList.add('hidden');
    this.el.innerHTML = '';
    this.onFinish?.();
  }
}

export { STEPS as TUTORIAL_STEPS };
